/**
 * Safe full-document navigation / reload primitives.
 *
 * A full-document load (hard navigation OR `location.reload()`) on **native**
 * destroys the WebView document while PowerSync's `imki.sqlite` connection is
 * still open mid-sync. The native `@capacitor-community/sqlite` connection is
 * keyed globally by filename and survives the JS-context teardown, so the NEXT
 * document's fresh `PowerSyncDatabase` collides on the abandoned-but-locked
 * connection → "ExecuteSet: database is locked (code 5)", which then fails every
 * local write (e.g. createAnalysis) for the life of that document.
 *
 * Every place that hard-loads the document must therefore release the native
 * connection FIRST. These helpers are the single choke point: route all
 * `window.location.{reload,assign,replace,href=}` call sites through them so the
 * code-5 collision can't be reintroduced.
 *
 * Web has no shared native SQLite handle (PowerSync runs over IndexedDB, which
 * tolerates the cross-document handoff), so on web these are thin passthroughs.
 */
import { isNative } from '@/lib/platform'
import { beginExternalNavigation, closePowerSync } from '@/lib/powersync'

// Hard cap on how long we wait for the native connection to close before
// navigating anyway. A clean close is well under this; the cap guarantees a
// stuck close can never strand the user on a page they're trying to leave
// (important for the error/chunk recovery callers).
const CLOSE_TIMEOUT_MS = 1500

/**
 * Release PowerSync's native SQLite connection ahead of a full-document load.
 * Arms the construction gate (so nothing respawns in the gap) and closes the
 * connection, bounded by {@link CLOSE_TIMEOUT_MS}. No-op on web.
 *
 * Exposed (not just used internally) so the pre-bundle inline chunk-recovery
 * script in index.html can await it via `window.__zsReleasePowerSync`.
 */
export async function releaseForHardLoad(): Promise<void> {
  if (!isNative()) return
  beginExternalNavigation()
  try {
    await Promise.race([
      closePowerSync(),
      new Promise<void>((resolve) => setTimeout(resolve, CLOSE_TIMEOUT_MS)),
    ])
  } catch {
    /* navigate regardless */
  }
}

/** `window.location.reload()`, but releases the native PowerSync connection first. */
export async function hardReload(): Promise<void> {
  await releaseForHardLoad()
  window.location.reload()
}

/** `window.location.assign(url)`, but releases the native PowerSync connection first. */
export async function hardNavigate(url: string): Promise<void> {
  await releaseForHardLoad()
  window.location.assign(url)
}

/** `window.location.replace(url)`, but releases the native PowerSync connection first. */
export async function hardReplace(url: string): Promise<void> {
  await releaseForHardLoad()
  window.location.replace(url)
}
